import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
  Link,
  alpha,
} from '@mui/material';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import AddInformationInfo from './addInformationInfo';
import Attachments from './attachments';
import CustomReportValues from './customReportValues';
import Details from './details';
import DownloadReportButton from './downloadReportButton';
import { WebSocketMessage } from './messages';

import { gradientBorder } from '../../assets/styles/button.style';
import { secondarySentence } from '../../assets/styles/text.style';
import AudioNote from '../../assets/svg/audioIcon.svg';
import { generateMessageReportPDF } from '../../assets/utils/pdfs/ReportMessagesPDF';
import { generateViewReportPDF } from '../../assets/utils/pdfs/ViewReportPDF';
import { ClosedStatuses } from '../../constants/reportConstants';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import ErrorType from '../../interfaces/ErrorType';
import AdditionalInfoModalComponent from '../common/additionalInfoModalComponent';
import CloseReportModalComponent from '../common/closeReportModalComponent';
import DecryptionLoadingAnimation from '../common/decryptionLoadingAnimation';
import AudioPlayer from '../reportSubmission/voiceReport/audioPlayer';

interface FileFromReportDetailsProps {
  id: number;
  originalFileName: string;
  fileType: string;
  url: string;
}

interface AdditionalInformationTransformedProps {
  id: number;
  descriptionText: string;
  files: Array<FileFromReportDetailsProps>;
}

interface VoiceReport {
  file_type: string;
  id: number;
  original_file_name: string;
  url: string;
}

interface CountryInfo {
  code: string;
  name: string;
}

export interface WhistleReportCustomFieldValue {
  id: number;
  title: string;
  tenant_report_custom_field?: number;
  get_risk_score: Record<string, number | null>;
  value: string | string[];
  can_edit_risk_score: boolean;
  conditional_logic_data: {
    conditional_logic_title: string;
    conditional_logic_answer: string;
    risk_score: number | null;
  };
  conditional_logic_value: string;
}

export interface ReportDetailsProp {
  id: string;
  formType?: number;
  reportType: number;
  description: string;
  createdAt: string;
  updatedAt: string;
  files: Array<FileFromReportDetailsProps>;
  additionalInformation: Array<AdditionalInformationTransformedProps>;
  customFieldValues: WhistleReportCustomFieldValue[];
  dueDate: string;
  finalDueDate: string;
  referenceKey: string;
  conversation: string;
  status: number;
  voiceReport: VoiceReport | null;
  reportTypeName: string;
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
  userName: string;
  country: CountryInfo | null;
  statusName: string;
  tenantName: string;
  hasFeedbackBeenSubmitted: boolean;
  isWhistleblowerFeedbackEnabled: boolean;
}

interface ReportDetailsProps {
  reportDetails: ReportDetailsProp;
  setReportDetails: Dispatch<SetStateAction<ReportDetailsProp>>;
  isLoading: boolean;
  error: ErrorType;
  messageList: WebSocketMessage[];
  enableDownload: boolean;
}

export const ReportDetails = ({
  reportDetails,
  setReportDetails,
  isLoading,
  error,
  messageList,
  enableDownload,
}: ReportDetailsProps) => {
  const [closeReport, setCloseReport] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();

  //Extracts data from any cookie name available in the Cookie Document
  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Check if the cookie starts with the desired name
      if (cookie.indexOf(nameEQ) === 0) {
        // Get the value of the cookie
        const value = cookie.substring(nameEQ.length);
        return value;
      }
    }
    return null;
  };

  useEffect(() => {
    const wsAccessDev = localStorage.getItem('ws_access');
    if (wsAccessDev) {
      try {
        setAccessToken(wsAccessDev);
      } catch (errorMessage) {
        createToastNotification(
          'Could not establish connection for messages.',
          'error',
        );
      }
    } else {
      const extractedAccessToken = getCookie('ws_access_token');
      if (extractedAccessToken !== null) {
        try {
          setAccessToken(extractedAccessToken);
        } catch (errorMessage) {
          const formattedError = intl.formatMessage({
            defaultMessage: 'Error occurred with messages.',
          });
          createToastNotification(formattedError, 'error');
        }
      }
    }
  }, [intl, createToastNotification]);

  const handleClose = () => {
    setOpen(false);
  };

  const addAdditionalInformationHandler = () => {
    setOpen(true);
  };

  const handleClickReport = async () => {
    setCloseReport((value) => !value);
  };

  const toggleDownloadState = () => {
    setDownloading((prevValue) => !prevValue);
  };

  const downloadFileBlob = async (url: string) => {
    const response = await fetch(url);
    const fileBlob = await response.blob();
    return fileBlob;
  };

  const generateReportArchive = async () => {
    let files = [...reportDetails.files];
    if (reportDetails.additionalInformation.length > 0) {
      reportDetails.additionalInformation.forEach((information) => {
        if (information.files.length > 0) {
          files = [...files, ...information.files];
        }
      });
    }
    const zip = new JSZip();
    const documentsFolder = zip.folder('documents');
    const reportPDF = await generateViewReportPDF(reportDetails);
    documentsFolder?.file('GeneratedReport.pdf', reportPDF);

    if (messageList.length > 0) {
      const messagePDF = await generateMessageReportPDF(
        messageList,
        accessToken,
      );
      documentsFolder?.file('ChatTranscription.pdf', messagePDF);
    }
    if (files.length > 0) {
      const filesFolder = zip.folder('files');
      const filePromises = files.map((file) => {
        return downloadFileBlob(file.url);
      });
      Promise.all(filePromises).then((retrievedFiles) => {
        if (retrievedFiles.length > 0) {
          retrievedFiles.forEach((file, index) => {
            filesFolder?.file(files[index].originalFileName, file);
          });
        }
        zip.generateAsync({ type: 'blob' }).then((content) => {
          toggleDownloadState();
          saveAs(content, 'ConfideReport.zip');
        });
      });
    } else {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        toggleDownloadState();
        saveAs(content, 'ConfideReport.zip');
      });
    }
  };

  const handleDownloadClicked = () => {
    toggleDownloadState();
    generateReportArchive();
  };

  let content = (
    <p>
      <FormattedMessage defaultMessage='Found no content.' />
    </p>
  );
  if (reportDetails) {
    const isReportClosed =
      reportDetails?.status !== null
        ? ClosedStatuses.includes(reportDetails.status)
        : false;
    content = (
      <Stack direction='column' spacing={2}>
        <AdditionalInfoModalComponent open={open} handleClose={handleClose} />
        <Card
          sx={{
            border: '1px solid #00000029',
            boxShadow: '0px 0px 4px 0px #0000000A',
            background: isReportClosed
              ? alpha('#181818', 0.04)
              : 'linear-gradient(0deg, #FFFFFF, #FFFFFF)',
            borderRadius: '12px',
            overflow: 'auto',
            height: {
              sm: 'calc((100vh - 56px) - 148px)',
              lg: 'calc((100vh - 64px) - 148px)',
            },
          }}
        >
          <Box sx={{ padding: '1.8rem 1.6rem' }}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '22px',
                  letterSpacing: '0rem',
                  textAlign: 'left',
                  color: '#000000',
                }}
              >
                <FormattedMessage defaultMessage='Your Report' />
              </Typography>
              {enableDownload && (
                <DownloadReportButton
                  handleButtonClicked={handleDownloadClicked}
                  isDownloading={isDownloading}
                />
              )}
            </Stack>
            <Details details={reportDetails.description} />
            <CustomReportValues values={reportDetails.customFieldValues} />
            <Attachments files={reportDetails.files} />
            {reportDetails.voiceReport && (
              <Box width='100%'>
                <Stack
                  direction='row'
                  marginY={2}
                  justifyContent='space-between'
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    flexGrow={1}
                    alignItems='center'
                  >
                    <img src={AudioNote} alt='Music' />
                    <Typography>
                      <FormattedMessage defaultMessage='Voice Record Memo' />
                    </Typography>
                  </Stack>
                </Stack>
                <AudioPlayer audioURL={reportDetails.voiceReport.url} />
                <Divider
                  flexItem
                  sx={{
                    marginTop: 2,
                  }}
                />
              </Box>
            )}
            <Divider light />
            {reportDetails?.additionalInformation &&
              reportDetails?.additionalInformation.length > 0 &&
              reportDetails?.additionalInformation.map(
                (item: AdditionalInformationTransformedProps, index) => {
                  return (
                    <Box key={item.id}>
                      <Details details={item.descriptionText} />
                      <Attachments files={item.files} />
                      {(index <
                        reportDetails.additionalInformation.length - 1 ||
                        reportDetails.additionalInformation.length === 0) && (
                        <Divider light />
                      )}
                    </Box>
                  );
                },
              )}
          </Box>

          {reportDetails?.additionalInformation.length === 0 &&
            !isReportClosed && (
              <Box sx={{ padding: 4 }}>
                <AddInformationInfo />
              </Box>
            )}

          {isReportClosed && (
            <Typography
              sx={{
                padding: 4,
                fontSize: '28px',
                fontWeight: '500',
                color: '#FF5E56',
                textAlign: 'center',
                lineHeight: '34.56px',
              }}
            >
              <FormattedMessage defaultMessage='Report Closed' />
            </Typography>
          )}
        </Card>
        {reportDetails?.additionalInformation.length !== 0 && (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Stack direction='row' justifyContent='space-between'>
              <Stack
                direction='column'
                alignItems='left'
                justifyContent='flex-end'
                spacing={1.5}
              >
                {!isReportClosed && (
                  <Stack>
                    <Typography
                      sx={{
                        paddingBottom: '1rem',
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '.875rem',
                        fontWeight: 500,
                        lineHeight: '1.188rem',
                        letterSpacing: '0rem',
                        textAlign: 'left',
                        color: '#C0C0C0',
                      }}
                      variant='body2'
                    >
                      <FormattedMessage defaultMessage='Do you wish to add additional information?' />
                    </Typography>
                    <Button
                      disabled={isReportClosed}
                      variant='outlined'
                      onClick={addAdditionalInformationHandler}
                      sx={{ ...gradientBorder, width: '9.375rem' }}
                    >
                      <FormattedMessage defaultMessage='Add' />
                    </Button>
                  </Stack>
                )}
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end', // Align children to the right
                }}
              >
                <CloseReportModalComponent
                  open={closeReport}
                  handleClose={handleClickReport}
                  setReportDetails={setReportDetails}
                />
                <Typography
                  component='div'
                  // mt={3}
                  sx={{
                    paddingBottom: '1rem',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '.875rem',
                    fontWeight: 500,
                    lineHeight: '1.188rem',
                    letterSpacing: '0rem',
                    textAlign: 'left',
                    color: '#C0C0C0',
                  }}
                >
                  <FormattedMessage defaultMessage='If you believe this report was submitted in error.' />
                </Typography>
                <Link
                  disabled={isReportClosed}
                  component='button'
                  sx={{
                    color: 'black',
                    textDecoration: 'none',
                    width: 'fit-content',
                    padding: '.5rem 1rem',
                    borderRadius: '2.0625rem',
                    height: '2.5rem',
                    border: `1px solid ${
                      !isReportClosed ? '#2449FC' : '#FF5E56'
                    }`,
                  }}
                  onClick={handleClickReport}
                >
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    // sx={{ width: '6.25rem' }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        lineHeight: '1.0625rem',
                        letterSpacing: '0rem',
                        textAlign: 'left',
                      }}
                    >
                      <FormattedMessage defaultMessage='Close report' />
                    </Typography>
                    <SignalCellular4BarIcon
                      sx={{
                        fontSize: 'smaller',
                        transform: 'rotate(270deg)', // Rotate the icon 180 degrees
                      }}
                    />
                  </Stack>
                </Link>
              </Box>
            </Stack>
          </Box>
        )}
        {reportDetails?.additionalInformation.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end', // Align children to the right
            }}
          >
            <CloseReportModalComponent
              open={closeReport}
              handleClose={handleClickReport}
              setReportDetails={setReportDetails}
            />
            <Typography
              component='div'
              // mt={3}
              sx={{
                ...secondarySentence,
                textAlign: 'right',
                width: 'fit-content',
                paddingBottom: '1rem',
              }}
            >
              <FormattedMessage defaultMessage='If you believe this report was submitted in error.' />
            </Typography>
            <Link
              disabled={isReportClosed}
              component='button'
              sx={{
                color: 'black',
                textDecoration: 'none',
                width: 'fit-content',
                height: '2.5rem',
                border: `1px solid ${!isReportClosed ? '#2449FC' : '#FF5E56'}`,
                padding: '.5rem 1rem',
                borderRadius: '2.0625rem',
              }}
              onClick={handleClickReport}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                // sx={{ width: '6.25rem' }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    lineHeight: '1.0625rem',
                    letterSpacing: '0rem',
                    textAlign: 'left',
                  }}
                >
                  <FormattedMessage defaultMessage='Close report' />
                </Typography>
                <SignalCellular4BarIcon
                  sx={{
                    fontSize: 'smaller',
                    transform: 'rotate(270deg)', // Rotate the icon 180 degrees
                  }}
                />
              </Stack>
            </Link>
          </Box>
        )}
      </Stack>
    );
  }

  if (error?.errorType) {
    if (error?.message) {
      content = <p>{error?.message}</p>;
    } else {
      content = (
        <p>
          <FormattedMessage defaultMessage='Error getting content' />
        </p>
      );
    }
  }

  if (isLoading) {
    content = (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <DecryptionLoadingAnimation />
      </Box>
    );
  }
  return <>{content}</>;
};

export default ReportDetails;
